import React, { useState, useEffect, useContext } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useMessages } from "./StatusMessages";
import PaymentPage from "./PaymentPage";
import Footer from "../../components/molecules/Footer/Footer";
import { Header } from "../../components/molecules/Header/Header";
import Container from "../../components/layout/Container/Container";
import Banner from "../../components/layout/Sections/Banner";
import fetchTradeFeesMaster from "../../scripts/tradeFeesMaster";
import Loader from "../../components/molecules/Loader";
import {
  submitPayment,
  submitPaymentIntent,
  getFeeByInspectionCaseNumber,
  getPaymentStatus,
} from "../../api/apimethods";
import { AppContext, defaultAppContextData } from "../../services/appData";
import { useContextService } from "../../services";
import { TradeFees } from "../../typings/models/TradeFees";
import { IPaymentData } from "../../typings/interfaces/Payment/PaymentData";
interface PaymentLineItem {
  paymentLineItemId?: string;
  feeCode?: string;
  unitPrice?: number;
  quantity?: number;
  amount?: number;
  amountWithoutTax?: number;
  taxAmount?: number;
  taxDescription?: string;
  name?: string;
}

interface InspectionPayment {
  paymentId?: string[];
  receiptNumber?: string;
  paymentLineItem?: PaymentLineItem[];
  totalAmountWithoutTax?: number;
  totalTaxAmount?: number;
  totalAmount?: number;
}

const isValidUenWithRunning = (value: string): boolean => {
  if (!value.trim() || !/^[A-Za-z0-9]+$/.test(value)) {
    return false;
  }

  if (value.length !== 21 && value.length !== 22) {
    return false;
  }

  const uenLength = value.length === 21 ? 9 : 10;
  const uen = value.substring(0, uenLength);
  const running = value.substring(uenLength);

  if (!/^[A-Za-z0-9]+$/.test(uen) || running.length !== 12 || !/^\d+$/.test(running)) {
    return false;
  }

  const datePart = running.substring(0, 8);
  const year = Number(datePart.substring(0, 4));
  const month = Number(datePart.substring(4, 6));
  const day = Number(datePart.substring(6, 8));
  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    isLeapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];

  return (
    year >= 1 &&
    year <= 9999 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth[month - 1]
  );
};

const CardForm = () => {
  const { getContextValueFromStorage, putContextUser } = useContextService();
  const [contextData] = useState(() =>
    getContextValueFromStorage(defaultAppContextData)
  );
  const user = contextData.user;
  const stripe = useStripe();
  const elements = useElements();
  const [messages, addMessage] = useMessages();
  const [paymentSuccessMessages, addPaymentSuccessMessages] = useMessages();
  const [feeMessages, setFeeMessages] = useMessages();
  const [uenMessages, setUenMessages] = useMessages();
  const [paymentMethodMessages, setPaymentMethodMessages] = useMessages();
  const [amount, setAmount] = useState(0);
  const [correctAmount, setCorrectAmount] = useState(0);
  const [currency, setCurrency] = useState("sgd");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [selectedModule, setSelectedModule] = useState("1");
  const [selectedIdType, setSelectedIdType] = useState("1");
  const [maxLengthReferenceNumber, setMaxLengthReferenceNumber] = useState(50);
  const [minLengthReferenceNumber, setMinLengthReferenceNumber] = useState(1);
  const [selectedIdTypeText, setSelectedIdTypeText] = useState("UEN");
  const [paymentMethodSelected, setPaymentMethodSelected] = useState("card");
  const [selectedTrade, setSelectedTrade] = useState("");
  const [selectedTradeAmount, setSelectedTradeAmount] = useState("3.50");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [uenNumber, setUenNumber] = useState("");
  const [inspectionCaseNumber, setInspectionCaseNumber] = useState("");
  const [isDetailsEnabled, setIsDetailsEnabled] = useState(true);
  const [isPaynowEnabled, setIsPaynowEnabled] = useState(false);
  const [isValidPaymentMethod, setValidPaymentMethod] = useState(false);
  const [isValidUen, setValidUen] = useState(true);
  const [isValidPayment, setValidPayment] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tradeFees, setTradeFees] = useState<TradeFees[]>([]);
  const [inspectionPaymentDetails, setInspectionPaymentDetails] =
    useState<InspectionPayment | null>(null);
  const [paymentIntentDetail, setPaymentIntentDetail] =
    useState<IPaymentData | null>(null);
  const { resetContextApplicationPayment } = useContextService();
  const [isVisible] = useState(false);
  const [paymentType] = useState(1);
  const [namelabel, setNameLabel] = useState("Name");
  const [emaillabel, setEmailLabel] = useState("Email");
  const [idtypelabel, setIDTypeLabel] = useState("ID Type");
  const [idtypetextlabel, setIDTypeTextLabel] = useState("");
  const appContext = useContext(AppContext);
  const applicationPayment = appContext.payment ?? null;
  const isFormValid = () => {
    if (selectedModule == "3") {
      if (
        inspectionCaseNumber.length > 0 &&
        referenceNumber.length > 0 &&
        uenNumber.length > 0 &&
        selectedTrade.length > 0 &&
        amount > 0 &&
        name.length > 0 &&
        email.length > 0 &&
        isValidPayment == false &&
        isValidUen == true &&
        isValidUenWithRunning(referenceNumber)
      ) {
        return true;
      }
      return false;
    } else {
      if (
        inspectionCaseNumber.length > 0 &&
        referenceNumber.length > 0 &&
        amount > 0 &&
        name.length > 0 &&
        email.length > 0 &&
        isValidPayment == false
      ) {
        return true;
      }
      return false;
    }
  };

  const sanitizeHtml = (html: string): string => {
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .replace(/\son\w+="[^"]*"/gi, "")
      .replace(/\son\w+='[^']*'/gi, "")
      .replace(/javascript:/gi, "");
  };

  const formatDescription = (description: string | null) => {
    if (!description) return "";
    const sanitized = sanitizeHtml(description);
    return sanitized
      .replace(/<i>/g, "<em>")
      .replace(/<\/i>/g, "</em>")
      .replace(/<li>/g, "<i>")
      .replace(/<\/li>/g, "</i>");
  };
  const validateForm = () => {
    if (selectedModule == "3") {
      if (
        inspectionCaseNumber.length == 0 ||
        referenceNumber.length == 0 ||
        uenNumber.length == 0 ||
        selectedTrade.length == 0 ||
        amount == 0 ||
        name.length == 0 ||
        email.length == 0 ||
        isValidPayment == true ||
        isValidUen == false ||
        !isValidUenWithRunning(referenceNumber)
      ) {
        setIsPaynowEnabled(false);
        setIsDetailsEnabled(true);
      } else {
        setIsPaynowEnabled(true);
        setIsDetailsEnabled(true);
      }
    } else {
      if (
        inspectionCaseNumber.length == 0 ||
        referenceNumber.length == 0 ||
        amount == 0 ||
        name.length == 0 ||
        email.length == 0 ||
        isValidPayment == true
      ) {
        setIsPaynowEnabled(false);
        setIsDetailsEnabled(true);
      } else {
        setIsPaynowEnabled(true);
        setIsDetailsEnabled(true);
      }
    }
  };

  const handlePaymentChangeChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const value = event.target.value;
      validateForm();
      setPaymentMethodMessages({ type: "CLEAR" });
      if (value === "paynow" && !isFormValid()) {
      setIsPaynowEnabled(false);
      setPaymentMethodMessages({
          type: "ADD",
          message:
            "Fill all required fields before proceeding to PayNow.",
        });
      setValidPaymentMethod(false);
      return;
    }
    setValidPaymentMethod(true);
      if (value === "paynow") {
        let moduleText = "Inspection";
    if (selectedModule === "1") {
      moduleText = "Inspection";
    } else if (selectedModule === "2") {
      moduleText = "Laboratory";
    } else if (selectedModule === "3") {
      moduleText = "Tradenet";
    } else if (selectedModule === "4") {
      moduleText = "Microchip Registration for CITES";
    } else if (selectedModule === "5") {
      moduleText = "Licence / Application";
    }
    const paymentData = {
      paymentType,
      amount,
      currency,
      email,
      name,
      selectedModule: moduleText,
      referenceNumber,
      uenNumber,
      feeCode: selectedTrade,
      paymentIntentId: "",
      clientSecret: "",
      paymentMethod: "paynow",
    };

    setPaymentIntentDetail(paymentData);
        setPaymentMethodSelected("paynow");
      } else {
        setPaymentMethodSelected("card");
      }
  };

  const handleInspectionCaseNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setInspectionCaseNumber(event.target.value);
    setReferenceNumber(event.target.value);

    if (event.target.value.includes(' ')) {
      setFeeMessages({ type: "CLEAR" });
      setFeeMessages({
          type: "ADD",
          message:
            "Tradenet unique reference number - Should not contain spaces",
        });
        validateForm();
        return;
    }
    else{
      setFeeMessages({ type: "CLEAR" });
    }

    const restrictedValues = ["P-COM", "A-ENV", "A-BIO", "A-AQU", "A-TER", "A-AVI"];

    if (!restrictedValues.some(val => event.target.value.toLowerCase().includes(val.toLowerCase())) && event.target.value.length > 15)
    {
        setNameLabel("Importer / Exporter Name");
        setEmailLabel("Importer / Exporter Email");
        setIDTypeLabel("Importer / Exporter ID Type");
        setIDTypeTextLabel("Importer / Exporter ");
    }
    else
    {
        setNameLabel("Name");
        setEmailLabel("Email");
        setIDTypeLabel("ID Type");
        setIDTypeTextLabel("");
    }

    if (selectedModule == "3") {
      setIsDetailsEnabled(false);
      setFeeMessages({ type: "CLEAR" });
      if (!isValidUenWithRunning(event.target.value)) {
        setValidPayment(true);
        setFeeMessages({
          type: "ADD",
          message:
            "Tradenet unique reference number must contain a valid UEN and a 12-digit running number with a valid YYYYMMDD date.",
        });
        validateForm();
        return;
      }
    }
    setValidPayment(false);
    setIsDetailsEnabled(true);
    validateForm();
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    validateForm();
    if (isFormValid()) {
      setIsPaynowEnabled(true);
    } else {
      setIsPaynowEnabled(false);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    validateForm();
    if (isFormValid()) {
      setIsPaynowEnabled(true);
    } else {
      setIsPaynowEnabled(false);
    }
  };

  const handleUenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUenNumber(event.target.value);
    validateForm();
    if (isFormValid()) {
      setIsPaynowEnabled(true);
    } else {
      setIsPaynowEnabled(false);
    }
  };

  const handleModuleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedModule(event.target.value);
    setInspectionCaseNumber("");
    setReferenceNumber("");
    if (event.target.value == "3") {
      setAmount(Number(selectedTradeAmount));
      setCorrectAmount(Number(selectedTradeAmount));
      setMaxLengthReferenceNumber(22);
      setMinLengthReferenceNumber(22);
    } else {
      setAmount(Number(0));
      setCorrectAmount(Number(0));
    }
    setInspectionPaymentDetails(null);
    validateForm();
  };

  const handleIdTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedIdType(event.target.value);
    setSelectedIdTypeText(event.target.selectedOptions[0].text);
    setValidUen(true);
    setUenMessages({ type: "CLEAR" });
    setUenNumber("");
    setIsDetailsEnabled(true);
    setIsPaynowEnabled(false);
  };

  const handleTradeAmountChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedTrade(event.target.value);
    validateForm();    
    const filteredFees = tradeFees.filter(
      (fee) => fee.feeCode === event.target.value
    );
    const filteredFee = filteredFees[0];
    if (filteredFee) {
      setSelectedTradeAmount(filteredFee?.fee?.toString());
      setAmount(filteredFee?.fee);
      setCorrectAmount(filteredFee?.fee);
    } else {
      setSelectedTradeAmount("0");
      setAmount(0);
      setCorrectAmount(0);
      validateForm();
    }
    setIsPaynowEnabled(event.target.value !== "");
    setFeeMessages({ type: "CLEAR" });
    if (!inspectionCaseNumber) {
      setFeeMessages({
        type: "ADD",
        message: "Please enter an Reference / Case number.",
      });
      return;
    }
    if (!isFormValid()) {
      setIsPaynowEnabled(false);
      return;
    }
  };

  const handleFetchFee = async () => {
    setFeeMessages({ type: "CLEAR" });
    let amountTotal: number = 0;
    if (!inspectionCaseNumber) {
      setFeeMessages({
        type: "ADD",
        message: "Please enter reference number / case number.",
      });
      validateForm();
      setIsPaynowEnabled(false);
      return;
    }
    try {
      setIsLoading(true);
      setIsDetailsEnabled(false);
      const statusResponse = await getPaymentStatus(inspectionCaseNumber);
      if (statusResponse.data === null) {
        throw new Error("An error occurred while fetching the payment status.");
      }
      setSelectedModule(statusResponse.data.moduleId);
      if (statusResponse.data.moduleId == "1") {
        setIsPaynowEnabled(true);
      } else {
        setIsPaynowEnabled(false);
      }
      setValidPayment(statusResponse.data.isPaid);
      validateForm();
      if (statusResponse.data.isPaid) {
        setFeeMessages({
          type: "ADD",
          message: statusResponse.data?.remarks,
        });
        setIsLoading(false);
        setIsPaynowEnabled(false);
        return;
      }
      setFeeMessages({
        type: "ADD",
        message: statusResponse.data?.remarks,
      });
      const feeResponse = await getFeeByInspectionCaseNumber(
        inspectionCaseNumber
      );
      if (feeResponse.data.error) {
        setValidPayment(true);
        validateForm();
        setIsPaynowEnabled(false);
        setInspectionPaymentDetails(null);
        setAmount(Number(0));
        setCorrectAmount(Number(0));
        throw new Error(feeResponse.data.error);
      }

      if (feeResponse.data.data == null) {
        setValidPayment(true);
        setFeeMessages({
          type: "ADD",
          message: "Reference number / case number not found.",
        });
        setIsLoading(false);
        setInspectionPaymentDetails(null);
        setAmount(Number(0));
        setCorrectAmount(Number(0));
        setIsPaynowEnabled(false);
        validateForm();
        return;
      }

      if (Number(feeResponse.data.data.totalAmount) == 0) {
        if (feeResponse.data.data.moduleId == "3") {
          setValidPayment(false);
          setIsLoading(false);
          setInspectionPaymentDetails(null);
          setSelectedTrade("");
          setSelectedTradeAmount("0");
          setAmount(0);
          setCorrectAmount(0);
          setIsPaynowEnabled(true);
          validateForm();
        } else {
          setValidPayment(true);
          if (feeResponse.data.data.message) {
            setFeeMessages({
              type: "ADD",
              message: feeResponse.data.data.message,
            });
          } else {
            setFeeMessages({
              type: "ADD",
              message:
                "Payment record not generated for this reference number / case number.",
            });
          }
          setIsLoading(false);
          setInspectionPaymentDetails(null);
          setAmount(Number(0));
          setCorrectAmount(Number(0));
          validateForm();
          setIsPaynowEnabled(false);
        }

        return;
      }
      setSelectedModule(feeResponse.data.data.moduleId ?? "1");
      setAmount(Number(feeResponse.data.data.totalAmount) || 0);
      amountTotal = Number(feeResponse.data.data.totalAmount) || 0;
      setCorrectAmount(Number(feeResponse.data.data.totalAmount) || 0);
      setReferenceNumber(inspectionCaseNumber);
      setInspectionPaymentDetails(feeResponse.data.data);
      if (Number(feeResponse.data.data.totalAmount) > 0) {
        setIsDetailsEnabled(true);
        setAmount(Number(feeResponse.data.data.totalAmount) || 0);
        setCorrectAmount(Number(feeResponse.data.data.totalAmount) || 0);
      }
      if (feeResponse.data.data.moduleId == "1") {
        setIsPaynowEnabled(true);
      } else if (feeResponse.data.data.moduleId == "2") {
        setIsPaynowEnabled(true);
      } else if (feeResponse.data.data.moduleId == "4") {
        setIsPaynowEnabled(true);
      } else if (feeResponse.data.data.moduleId == "5") {
        setIsPaynowEnabled(true);
      } else {
        setIsPaynowEnabled(false);
      }
    } catch (error) {
      setValidPayment(true);
      validateForm();
      setIsPaynowEnabled(false);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred while fetching the fee.";
      setFeeMessages({ type: "ADD", message: errorMessage });
    }
    setIsLoading(false);
    validateForm();
    setValidPayment(false);
    if (selectedModule == "3") {
      if (
        inspectionCaseNumber.length > 0 &&
        referenceNumber.length > 0 &&
        uenNumber.length > 0 &&
        selectedTrade.length > 0 &&
        amount > 0 &&
        name.length > 0 &&
        email.length > 0 &&
        isValidUen == true
      ) {
        setIsPaynowEnabled(true);
      } else {
        setIsPaynowEnabled(false);
      }
    } else {
      if (
        inspectionCaseNumber.length > 0 &&
        referenceNumber.length > 0 &&
        (amount > 0 || amountTotal > 0) &&
        name.length > 0 &&
        email.length > 0
      ) {
        setIsPaynowEnabled(true);
      } else {
        setIsPaynowEnabled(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    addMessage({ type: "CLEAR" });
    if (!stripe || !elements) {
      addMessage({ type: "ADD", message: "Stripe.js has not yet loaded." });
      setIsLoading(false);
      return;
    }
    if (amount == 0) {
      addMessage({ type: "ADD", message: "Amount is required." });
      setIsLoading(false);
      return;
    }

    if (referenceNumber.length == 0) {
      addMessage({ type: "ADD", message: "Reference number is required." });
      setIsLoading(false);
      return;
    }

    if (name.length == 0) {
      addMessage({ type: "ADD", message: "Name is required." });
      setIsLoading(false);
      return;
    }

    if (email.length == 0) {
      addMessage({ type: "ADD", message: "Email is required." });
      setIsLoading(false);
      return;
    }

    if (selectedModule == "3" && !isValidUenWithRunning(referenceNumber)) {
      addMessage({
        type: "ADD",
        message: "Tradenet unique reference number is invalid.",
      });
      setIsLoading(false);
      return;
    }

    if (selectedModule == "3" && uenNumber.length == 0) {
      addMessage({ type: "ADD", message: "Uen number is required." });
      setIsLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      addMessage({ type: "ADD", message: "Card element not found." });
      setIsLoading(false);
      return;
    }

    if (amount != correctAmount) {
      addMessage({
        type: "ADD",
        message: "Wrong amount entered. Payment Proceed done.",
      });
      setIsLoading(false);
      return;
    }
    let moduleText = "Inspection";
    if (selectedModule === "1") {
      moduleText = "Inspection";
    } else if (selectedModule === "2") {
      moduleText = "Laboratory";
    } else if (selectedModule === "3") {
      moduleText = "Tradenet";
    } else if (selectedModule === "4") {
      moduleText = "Microchip Registration for CITES";
    } else if (selectedModule === "5") {
      moduleText = "Licence / Application";
    }
    const paymentData = {
      paymentType,
      amount,
      currency,
      email,
      name,
      selectedModule: moduleText,
      referenceNumber,
      uenNumber,
      feeCode: selectedTrade,
      paymentIntentId: "",
      clientSecret: "",
      paymentMethod: "card",
    };

    try {
      setIsLoading(true);
      const response = await submitPaymentIntent(paymentData);
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(response.data.clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              email,
              name,
            },
          },
        });

      if (confirmError) {
        addMessage({
          type: "ADD",
          message:
            confirmError.message ??
            "An error occurred while confirming the payment.",
        });
        setIsLoading(false);
        return;
      }

      paymentData.clientSecret = response?.data?.clientSecret;
      paymentData.paymentIntentId = response?.data?.paymentIntentId;
      await submitPayment(paymentData);
      resetContextApplicationPayment();
      setIsLoading(false);
      var messages = `Payment ${paymentIntent.status}: ${paymentIntent.id}`;
      addPaymentSuccessMessages({ type: "ADD", message: messages });
      window.location.href = `/eservices/success?transaction=${
        paymentIntent.id
      }&reference=${referenceNumber}&name=${encodeURIComponent(
        name
      )}&email=${encodeURIComponent(email)}&amount=${encodeURIComponent(
        amount
      )}&dtpayment=${paymentIntent.created}`;
    } catch (error) {
      setIsLoading(false);
      const message =
        error instanceof Error
          ? error.message
          : "An error occurred while processing the payment.";
      addMessage({ type: "ADD", message: message });
    }

  };

  useEffect(() => {
    const fetchTradeFeesData = async () => {
      const tradeFeesData = await fetchTradeFeesMaster();
      setTradeFees(tradeFeesData);
    };
    fetchTradeFeesData();
  }, []);

  useEffect(() => {
    if (applicationPayment?.applicationNumber != null) {
      setInspectionCaseNumber(applicationPayment.applicationNumber);
      setReferenceNumber(applicationPayment.applicationNumber);
      setEmail(applicationPayment.email ?? "");
      setName(applicationPayment.name ?? "");
    }
  }, [applicationPayment?.applicationNumber]);

  return (
    <>
      <Header />
      <Container>
        <Banner name="Payment" uiClass="banner-bg-content" />
        <div className="inst-cnt mt-4 mb-2">
          <div className="inner-content">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label
                      htmlFor="inspectionCaseNumber"
                      className="form-label"
                    >
                      Reference Number / Case Number / Unique Reference number
                      <span className="star-red">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="inspectionCaseNumber"
                      value={inspectionCaseNumber}
                      onChange={handleInspectionCaseNumberChange}
                      onBlur={handleInspectionCaseNumberChange}
                      maxLength={maxLengthReferenceNumber}
                      minLength={minLengthReferenceNumber}
                      required
                    />
                    <div className="form-text small refnumber-small-hint">
  <i>Licence application number (e.g., FC251234567)</i>
  <br />

  <i>Laboratory application number (e.g., EP2025123456 / EA2025123456)</i>
  <br />

  <i>Tradenet unique reference number (e.g., 123456789Z202512030000)</i>
  <i> - format (e.g., (UEN)(YYYY)(MM)(DD)(4digits))</i>
  <br />

  <i>Inspection case number (e.g., INS-25-123456)</i>
  <br />

  <i>Microchip registration reference number (e.g., B-25-10-123456)</i>
</div>

                    <button
                      className="btn btn-primary mt-2"
                      onClick={handleFetchFee}
                      disabled={!isDetailsEnabled}
                    >
                      Calculate Fee
                    </button>
                  </div>
                  {amount > 0 && (
                    <div className="mb-3">
                      <label htmlFor="module" className="form-label">
                        Module
                      </label>
                      <select
                        className="form-select"
                        id="module"
                        value={selectedModule}
                        onChange={handleModuleChange}
                        disabled
                      >
                        <option value="1">Inspection</option>
                        <option value="2">Laboratory</option>
                        <option value="3">Tradenet</option>
                        <option value="4">
                          Microchip Registration for CITES
                        </option>
                        <option value="5">Licence / Application</option>
                      </select>
                    </div>
                  )}
                  {selectedModule == "3" && (
                    <div className="mb-3">
                      <label htmlFor="tradeamount" className="form-label">
                        Commodity<span className="star-red">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="tradeamount"
                        value={selectedTrade}
                        onChange={handleTradeAmountChange}
                        disabled={!isDetailsEnabled}
                        required
                      >
                        <option value="">
                          -- Select Amount --
                        </option>
                        {tradeFees.map((option) => (
                          <option key={option.feeCode} value={option.feeCode}>
                            {option.feeName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {selectedModule == "3" && selectedTrade && (
                    <div className="mb-3">
                      <label htmlFor="tradeamount" className="form-label">
                        Amount (S$)<span className="star-red">*</span>
                      </label>
                      {currency.toUpperCase()}{" "}
                      {Number(selectedTradeAmount).toFixed(2)}
                    </div>
                  )}

                  <div>
                    {!isValidPayment &&
                      feeMessages.map((message, index) => (
                        <div key={index} className="csp-text-green">
                          {message}
                        </div>
                      ))}
                    {isValidPayment &&
                      feeMessages.map((message, index) => (
                        <div key={index} className="csp-text-red">
                          {message}
                        </div>
                      ))}
                  </div>
                  {(selectedModule == "1" ||
                    selectedModule == "2" ||
                    selectedModule == "4" ||
                    selectedModule == "5") &&
                  isLoading ? (
                    <Loader />
                  ) : (
                    (selectedModule == "1" ||
                      selectedModule == "2" ||
                      selectedModule == "4" ||
                      selectedModule == "5") &&
                    amount > -1 && (
                      <div
                        className="fee-display-box mb-3 csp-fee-box"
                      >
                        <strong>Amount (S$): </strong>
                        {currency.toUpperCase()} {amount.toFixed(2)}
                      </div>
                    )
                  )}
                  {isLoading && <Loader />}
                  {(selectedModule == "1" ||
                    selectedModule == "2" ||
                    selectedModule == "4" ||
                    selectedModule == "5") &&
                    inspectionPaymentDetails && (
                      <div className="row justify-content-center mt-4">
                        <div className="col-md-12">
                          <table
                            className="table csp-table-scroll"
                          >
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Unit Price</th>
                                <th>Quantity</th>
                                {user?.role !== "ForeignerLogin" && (
                                  <th>Amount Without GST (S$)</th>
                                )}
                                {user?.role !== "ForeignerLogin" && (
                                  <th>GST Amount (S$)</th>
                                )}
                                <th>Amount (S$)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {inspectionPaymentDetails.paymentLineItem?.map(
                                (item, index) => (
                                  <tr key={index}>
                                    <td
                                      dangerouslySetInnerHTML={{
                                        __html: formatDescription(
                                          item.name || ""
                                        ),
                                      }}
                                    ></td>
                                    <td
                                      dangerouslySetInnerHTML={{
                                        __html: formatDescription(
                                          item.taxDescription || ""
                                        ),
                                      }}
                                    ></td>

                                    <td>{item.unitPrice?.toFixed(2)}</td>
                                    <td>{item.quantity}</td>
                                    {user?.role !== "ForeignerLogin" && (
                                      <td>
                                        {item.amountWithoutTax?.toFixed(2)}
                                      </td>
                                    )}
                                    {user?.role !== "ForeignerLogin" && (
                                      <td>{item.taxAmount?.toFixed(2)}</td>
                                    )}
                                    <td>{item.amount?.toFixed(2)}</td>
                                  </tr>
                                )
                              )}
                            </tbody>
                            <tfoot>
                              <tr>
                                <th colSpan={4}>Total (S$)</th>
                                {user?.role !== "ForeignerLogin" && (
                                  <td>
                                    {inspectionPaymentDetails.totalAmountWithoutTax?.toFixed(
                                      2
                                    )}
                                  </td>
                                )}
                                {user?.role !== "ForeignerLogin" && (
                                  <td>
                                    {inspectionPaymentDetails.totalTaxAmount?.toFixed(
                                      2
                                    )}
                                  </td>
                                )}
                                <td>
                                  {inspectionPaymentDetails.totalAmount?.toFixed(
                                    2
                                  )}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                </div>
                <div className="col-md-6">
                  <form
                    onSubmit={handleSubmit}
                    className={!isDetailsEnabled ? "disabled-form" : ""}
                  >      
                                      {/* Existing form elements go here, wrapped in a condition to check if isDetailsEnabled is true */}
                    {isVisible && (                
                      <div className="mb-3">
                        <label htmlFor="module" className="form-label">
                          Module
                        </label>
                        <select
                          className="form-select"
                          id="module"
                          value={selectedModule}
                          onChange={(e) => setSelectedModule(e.target.value)}
                          disabled={!isDetailsEnabled}
                          required
                        >                         
                          <option value="3">Inspection</option>                         
                        </select>
                      </div>
                                     )}
                    {isVisible && (
                      <div className="mb-3">
                        <label htmlFor="referenceNumber" className="form-label">
                          Reference Number
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="referenceNumber"
                          value={referenceNumber}
                          onChange={(e) => setReferenceNumber(e.target.value)}
                          disabled={true}
                          required
                        />
                      </div>
                                        )}
                    {isVisible && (
                      <div className="mb-3">
                        <label htmlFor="amount" className="form-label">
                          Amount (S$)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="amount"
                          value={amount}
                          onChange={(e) => setAmount(Number(e.target.value))}
                          disabled={true}
                          required
                        />
                      </div>
                                      )}
                    {isVisible && (
                      <div className="mb-3">
                        <label htmlFor="currency" className="form-label">
                          Currency
                        </label>
                        <select
                          className="form-select"
                          id="currency"
                          value={currency}
                          onChange={(e) => setCurrency(e.target.value)}
                          disabled={true}
                          required
                        >
                          <option value="sgd">SGD</option>
                        </select>
                      </div>
                      )}
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">
                        {namelabel}<span className="star-red">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        value={name}
                        onChange={handleNameChange}
                        onBlur={handleNameChange}
                        disabled={!isDetailsEnabled}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        {emaillabel}<span className="star-red">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        value={email}
                        onChange={handleEmailChange}
                        onBlur={handleEmailChange}
                        disabled={!isDetailsEnabled}
                        required
                      />
                    </div>

                    {selectedModule == "3" && (
                      <div>
                        <div className="mb-3">
                          <label htmlFor="idtype" className="form-label">
                            {idtypelabel}<span className="star-red">*</span>
                          </label>
                          <select
                            className="form-select"
                            id="idtype"
                            value={selectedIdType}
                            onChange={handleIdTypeChange}
                            required
                          >
                            <option value="1">UEN</option>
                            <option value="2">Identification Number</option>
                          </select>
                        </div>
                        <div className="mb-3">
                          <label htmlFor="uenNumber" className="form-label">
                            {idtypetextlabel}{selectedIdTypeText}
                            <span className="star-red">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="uenNumber"
                            value={uenNumber}
                            onChange={handleUenChange}
                            onBlur={handleUenChange}
                            disabled={!isDetailsEnabled}
                            required
                          />
                          <div>
                            {isValidUen &&
                              uenMessages.map((message, index) => (
                                <div key={index} className="csp-text-green">
                                  {message}
                                </div>
                              ))}
                            {!isValidUen &&
                              uenMessages.map((message, index) => (
                                <div key={index} className="csp-text-red">
                                  {message}
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mb-3">                      
                          <label htmlFor="paymentmethod" className="form-label">
                            Payment method<span className="star-red">*</span>
                          </label>
                          <div className="paymentmethod"> 
                            <label className="radio-option">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="card"
                              checked={paymentMethodSelected === "card"}
                              onChange={handlePaymentChangeChange}
                            />
                            <span className="radio-check"></span>
                            <span className="paymentmethod-text">Credit/Debit Card</span>
                            </label>
                          
                            <label className="radio-option">
                              <input
                                type="radio"
                                name="paymentMethod"
                                value="paynow"
                                checked={paymentMethodSelected === "paynow"}
                                onChange={handlePaymentChangeChange}
                              />
                              <span className="radio-check"></span>
                              <span className="paymentmethod-text">PayNow QR</span>                               
                            </label>
                          </div>
                          {!isValidPaymentMethod &&
                              paymentMethodMessages.map((message, index) => (
                                <div key={index} className="csp-text-red">
                                  {message}
                                </div>
                              ))}
                    </div>
                    {paymentMethodSelected == "card" && (
                    <div className="mb-3">
                      <label htmlFor="card-element" className="form-label">
                        Card Details<span className="star-red">*</span>
                      </label>
                      <CardElement
                        className="form-control"
                        id="card-element"
                        options={{ hidePostalCode: true }}
                      />
                    </div>)}
                    {paymentMethodSelected == "card" && (<button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!isPaynowEnabled}
                    >
                      Pay Now
                    </button>)}
                  </form>
                  {paymentMethodSelected == "paynow" && paymentIntentDetail != null && (
                  <PaymentPage paymentIntent={paymentIntentDetail} />)}
                  <div>
                    {messages.map((message, index) => (
                      <div key={index} className="csp-text-red">
                        {message}
                      </div>
                    ))}
                    {paymentSuccessMessages.map((message, index) => (
                      <div key={index} className="csp-text-green">
                        {message}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
      <Footer />
    </>
  );
};

export default CardForm;
